import { EnvironmentId } from "@heydex/contracts";
import * as Schema from "effect/Schema";

/** The runtime running in this page; its HTTP routes answer under the page's base path. */
export class PrimaryConnectionTarget extends Schema.TaggedClass<PrimaryConnectionTarget>()(
  "PrimaryConnectionTarget",
  {
    environmentId: EnvironmentId,
    label: Schema.String,
    httpBaseUrl: Schema.String,
  },
) {}

const ConnectionTransientReason = Schema.Literals(["timeout", "transport", "remote-unavailable"]);

const ConnectionBlockedReason = Schema.Literals(["configuration"]);

export class ConnectionTransientError extends Schema.TaggedError<ConnectionTransientError>()(
  "ConnectionTransientError",
  {
    reason: ConnectionTransientReason,
    detail: Schema.String,
    traceId: Schema.optionalKey(Schema.String),
  },
) {
  override get message(): string {
    return this.detail;
  }
}

export class ConnectionBlockedError extends Schema.TaggedError<ConnectionBlockedError>()(
  "ConnectionBlockedError",
  {
    reason: ConnectionBlockedReason,
    detail: Schema.String,
    traceId: Schema.optionalKey(Schema.String),
  },
) {
  override get message(): string {
    return this.detail;
  }
}

export type ConnectionAttemptError = ConnectionTransientError | ConnectionBlockedError;

export interface PreparedConnection {
  readonly environmentId: EnvironmentId;
  readonly label: string;
  readonly httpBaseUrl: string;
  readonly target: PrimaryConnectionTarget;
}

export type SupervisorConnectionPhase = "available" | "connecting" | "connected" | "blocked";

export type ConnectionAttemptStage = "opening" | "synchronizing";

export interface SupervisorConnectionState {
  readonly desired: boolean;
  readonly phase: SupervisorConnectionPhase;
  readonly stage: ConnectionAttemptStage | null;
  readonly attempt: number;
  readonly generation: number;
  readonly lastFailure: ConnectionAttemptError | null;
}

export type ConnectionProjectionPhase = "disconnected" | "synchronizing" | "ready";

export function connectionProjectionPhase(
  state: SupervisorConnectionState,
): ConnectionProjectionPhase {
  switch (state.phase) {
    case "connecting":
      return "synchronizing";
    case "connected":
      return "ready";
    case "available":
    case "blocked":
      return "disconnected";
  }
}

export const AVAILABLE_CONNECTION_STATE: SupervisorConnectionState = Object.freeze({
  desired: false,
  phase: "available",
  stage: null,
  attempt: 0,
  generation: 0,
  lastFailure: null,
});
